'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

interface RefreshHeaderProps {
  asOf: string; // ISO: server time (Schedule) or the last successful client fetch (Matches)
  /** Client-fetched pages pass their own reload; the Schedule re-renders the server page. */
  onRefresh?: () => void;
}

export function RefreshHeader({ asOf, onRefresh }: RefreshHeaderProps) {
  const t = useTranslations('PublicSchedule');
  const router = useRouter();
  // Defer the "now" timestamp until after mount to avoid SSR/CSR drift.
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const ageLabel = (() => {
    if (now === null) return t('refresh');
    const ageSec = Math.max(0, Math.round((now - new Date(asOf).getTime()) / 1000));
    return ageSec < 60
      ? t('updatedSecondsAgo', { seconds: ageSec })
      : t('updatedMinutesAgo', { minutes: Math.floor(ageSec / 60) });
  })();

  return (
    <div className="ml-auto flex items-center justify-end">
      <button
        type="button"
        onClick={() => (onRefresh ? onRefresh() : router.refresh())}
        className="bg-card text-foreground hover:bg-surface min-h-11 cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium whitespace-nowrap"
        aria-label={t('refresh')}
      >
        ↻ {ageLabel}
      </button>
    </div>
  );
}
